import { createFileRoute, notFound } from "@tanstack/react-router";
import { queryOptions } from "@tanstack/react-query";
import { buildHead } from "@/lib/seo/head";
import { SITE_URL } from "@/lib/seo/routeMeta";
import { contentHeaders } from "@/lib/seo/contentCache";
import { supabase } from "@/integrations/supabase/client";
import { getGuideBySlug } from "@/data/guides";
import { buildGuideJsonLd } from "@/lib/guideSchema";
import GuidePage from "@/pages/GuidePage";

export interface BlogGuideData {
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  cover_image_url: string | null;
  category: string;
  author_name: string | null;
  published_at: string | null;
}

const blogGuideQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["blog-guide", slug],
    queryFn: async (): Promise<BlogGuideData | null> => {
      const { data } = await supabase
        .from("blog_posts")
        .select(
          "title, slug, content, excerpt, cover_image_url, category, author_name, published_at",
        )
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();
      return (data as BlogGuideData | null) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

export const Route = createFileRoute("/guides/$slug")({
  // Static guides resolve synchronously from src/data/guides.ts. Blog-backed
  // guides are fetched here so their body is server-rendered rather than
  // appearing only after hydration.
  loader: async ({ params, context }) => {
    if (getGuideBySlug(params.slug)) return { blogGuide: null };
    const blogGuide = await context.queryClient.ensureQueryData(
      blogGuideQueryOptions(params.slug),
    );
    if (!blogGuide) throw notFound();
    return { blogGuide };
  },
  // Database-backed guides render live on each cache miss so a new guide is
  // crawlable without a deploy. See src/lib/seo/contentCache.ts.
  headers: () => contentHeaders(true),
  head: ({ params, loaderData }) => {
    const canonical = `${SITE_URL}/guides/${params.slug}`;
    const guide = getGuideBySlug(params.slug);

    if (guide) {
      const title = `${guide.title} | Home Approach`;
      const description = guide.subtitle || guide.title;
      return buildHead({
        title,
        description,
        canonical,
        ogTitle: guide.title,
        ogDescription: description,
        ogType: "article",
        jsonLd: buildGuideJsonLd(guide) as unknown as Record<string, unknown>[],
      });
    }

    const blogGuide = loaderData?.blogGuide;
    if (!blogGuide) {
      return buildHead({
        title: "Guide unavailable | Home Approach",
        description: "This guide could not be loaded.",
        canonical,
        ogTitle: "Guide unavailable | Home Approach",
        ogDescription: "This guide could not be loaded.",
        ogType: "article",
        noIndex: true,
      });
    }

    const description =
      blogGuide.excerpt || `Read ${blogGuide.title} on Home Approach.`;
    const image = blogGuide.cover_image_url
      ? blogGuide.cover_image_url.startsWith("http")
        ? blogGuide.cover_image_url
        : `${SITE_URL}${blogGuide.cover_image_url}`
      : undefined;

    // A database-backed guide is the same article that is served, server-rendered and listed in the sitemap, at
    // /blog/<slug>. That URL is the canonical one so search engines do not see two competing copies.
    return buildHead({
      title: `${blogGuide.title} | Home Approach`,
      description,
      canonical: `${SITE_URL}/blog/${blogGuide.slug}`,
      ogTitle: blogGuide.title,
      ogDescription: description,
      ogType: "article",
      image,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: blogGuide.title,
        description,
        datePublished: blogGuide.published_at ?? undefined,
        author: {
          "@type": "Person",
          name: blogGuide.author_name || "Home Approach Team",
        },
        publisher: {
          "@type": "Organization",
          name: "Home Approach",
          url: SITE_URL,
          logo: { "@type": "ImageObject", url: `${SITE_URL}/og-image.png` },
        },
        mainEntityOfPage: `${SITE_URL}/blog/${blogGuide.slug}`,
      },
    });
  },
  component: GuidePage,
});
